/*
 * Bridge between TanStack Query mutation errors and the global toast
 * system. When a mutation throws a `ConflictError` with
 * `conflict_reason === "optimistic_version_mismatch"`, we:
 *
 *   1. silently auto-refetch the affected query keys (the parent hook
 *      must pass them — typically the resource being mutated);
 *   2. push the passive <ConflictToast> message (no action button).
 *
 * Decided 2026-05-17, UX-Q #2 — components.md §10.3.
 */

import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useCallback } from "react";
import { ConflictError } from "@/api/client";
import { useToast } from "@/components/toast/ToastProvider";

export function useConflictToast() {
  const qc = useQueryClient();
  const { push } = useToast();

  return useCallback(
    (err: unknown, keysToRefresh: QueryKey[] = []) => {
      if (err instanceof ConflictError && err.conflictReason === "optimistic_version_mismatch") {
        for (const key of keysToRefresh) {
          void qc.invalidateQueries({ queryKey: key });
        }
        push({
          tone: "warning",
          title: "Дані оновлено — перевір і спробуй ще раз.",
        });
        return true;
      }
      return false;
    },
    [qc, push],
  );
}
